import { computed, ref, toValue, watch, type ComputedRef, type MaybeRefOrGetter } from 'vue'

import type { FilterDropdownables } from '../models/filter.types'
import type { DropdownableDimension, WorkspaceSchedule } from '../models/schedule.types'

import { useReportSchedules } from './useReportSchedules'

export type ReportFilterDropdownables = FilterDropdownables
export type LoadReportFilterDropdownValues = (field: string) => Promise<string[]>

export interface UseReportFilterDropdownablesOptions {
  reportId: MaybeRefOrGetter<number>
  fields: MaybeRefOrGetter<readonly string[]>
  enabled?: MaybeRefOrGetter<boolean>
}

export interface UseReportFilterDropdownables {
  dropdownables: ComputedRef<ReportFilterDropdownables>
  loadDropdownValues: LoadReportFilterDropdownValues
}

function scheduleKey(reportId: number, schedule: WorkspaceSchedule | null): string {
  return schedule
    ? `${reportId}:${schedule.workspace_id}:${schedule.id}`
    : `${reportId}:no-schedule`
}

/** Resolve schedule metadata now and fetch a selected filter field's values only on demand. */
export function useReportFilterDropdownables(
  options: UseReportFilterDropdownablesOptions,
): UseReportFilterDropdownables {
  const schedules = useReportSchedules()
  const loadedValues = ref<Record<string, string[]>>({})
  const inflight = new Map<string, Promise<string[]>>()
  let generation = 0

  const enabled = computed(() => toValue(options.enabled ?? true))
  const schedule = computed(() =>
    enabled.value ? schedules.getScheduleByReportId(toValue(options.reportId)) : null,
  )
  const contextKey = computed(() => scheduleKey(toValue(options.reportId), schedule.value))
  const fields = computed(() => [...new Set(toValue(options.fields).filter(Boolean))].sort())
  const metadata = computed<Record<string, DropdownableDimension>>(() => {
    const currentSchedule = schedule.value
    if (!enabled.value || !currentSchedule) return {}
    return Object.fromEntries(
      fields.value.flatMap((field) => {
        const dimension = schedules.dropdownableDimensions(currentSchedule.id, field)
        if (!dimension) return []
        return [
          [
            field,
            {
              unique_count: dimension.unique_count,
              ...(dimension.values === undefined ? {} : { values: [...dimension.values] }),
            },
          ],
        ]
      }),
    )
  })
  const fieldsKey = computed(() => JSON.stringify(fields.value))
  const metadataKey = computed(() => JSON.stringify(metadata.value))
  const dropdownables = computed<ReportFilterDropdownables>(() => {
    const keys = new Set([...Object.keys(metadata.value), ...Object.keys(loadedValues.value)])
    return Object.fromEntries(
      [...keys].map((field) => {
        const dimension = metadata.value[field]
        const values = loadedValues.value[field] ?? dimension?.values
        return [
          field,
          {
            unique_count: dimension?.unique_count ?? values?.length ?? 0,
            ...(values === undefined ? {} : { values: [...values] }),
          },
        ]
      }),
    )
  })

  function resetLoadedValues(): void {
    generation += 1
    loadedValues.value = {}
    inflight.clear()
  }

  watch([contextKey, fieldsKey, metadataKey, enabled], resetLoadedValues, { flush: 'sync' })

  const loadDropdownValues: LoadReportFilterDropdownValues = async (field) => {
    const currentSchedule = schedule.value
    const dimension = metadata.value[field]
    if (!enabled.value || !currentSchedule || !fields.value.includes(field)) return []
    const existing = loadedValues.value[field] ?? dimension?.values
    if (existing !== undefined) return [...existing]

    const requestGeneration = generation
    const requestContext = contextKey.value
    const requestKey = `${requestContext}:${field}`
    let request = inflight.get(requestKey)
    if (!request) {
      request = schedules.loadDropdownValues(currentSchedule.id, field)
      inflight.set(requestKey, request)
    }

    try {
      const values = await request
      if (
        requestGeneration !== generation ||
        requestContext !== contextKey.value ||
        !fields.value.includes(field)
      ) {
        return []
      }
      loadedValues.value = { ...loadedValues.value, [field]: [...values] }
      return [...values]
    } catch (error) {
      if (requestGeneration !== generation || requestContext !== contextKey.value) return []
      throw error
    } finally {
      if (inflight.get(requestKey) === request) inflight.delete(requestKey)
    }
  }

  return { dropdownables, loadDropdownValues }
}
